import { useMemo } from "react";
import { Layers, X, Plus, Trash, Save, Users } from "lucide-react";
import type {
  CostConfig,
  ResourceValueDoc,
  AssignedResourceDoc,
} from "../../store/useAdminStore";
import type { ErpEmployee } from "../../store/useErpEmployeesStore";
import type { ProjectTypeConfig } from "../../store/useProjectTypeStore";

export const PROJECT_TYPES = [
  "Website",
  "Graphic Design",
  "Video Animation",
  "Marketing",
  "Trial-based",
];

export const TIERS = ["Basic", "Standard", "Premium"] as const;
export type Tier = (typeof TIERS)[number];

export type AccountOption = { _id: string; name: string };

export const blankResource = (): ResourceValueDoc => ({
  amount: 0,
  type: "fixed",
  hours: 0,
});

export const emptyConfig = (
  defaults?: Partial<CostConfig>
): Partial<CostConfig> => ({
  projectType: "Website",
  clientTier: "Standard",
  upworkAccount: null,
  fixedCost: blankResource(),
  rangeCost: { min: 0, max: 0 },
  graphicDesignerCost: blankResource(),
  coordinatorCost: blankResource(),
  commission: 10,
  upworkFee: 10,
  profitMarginPct: 0,
  otherFees: { label: "", amount: 0, type: "fixed" },
  extraRoles: [],
  notes: "",
  ...defaults,
});

interface Props {
  value: Partial<CostConfig>;
  accounts: AccountOption[];
  employees: ErpEmployee[];
  saving: boolean;
  onChange: (v: Partial<CostConfig>) => void;
  onSave: () => void;
  onClose: () => void;
  projectTypes?: string[];
  typeConfigs?: ProjectTypeConfig[];
}

const usd = (n: number) =>
  `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CostConfigDrawer = ({
  value,
  accounts,
  employees,
  saving,
  onChange,
  onSave,
  onClose,
  projectTypes,
  typeConfigs,
}: Props) => {
  const typeOptions =
    typeConfigs && typeConfigs.length
      ? typeConfigs.map((t) => t.name)
      : projectTypes && projectTypes.length
      ? projectTypes
      : PROJECT_TYPES;
  const activeSubTypes = useMemo(
    () =>
      (typeConfigs?.find((t) => t.name === value.projectType)?.subTypes || []).filter((s) => s.active),
    [typeConfigs, value.projectType]
  );
  const resources = value.assignedResources || [];

  const updateResourceRow = (i: number, patch: Partial<AssignedResourceDoc>) => {
    const next = [...resources];
    next[i] = { ...next[i], ...patch } as AssignedResourceDoc;
    onChange({ ...value, assignedResources: next });
  };
  const pickEmployee = (i: number, email: string) => {
    const emp = employees.find((e) => e.email === email);
    updateResourceRow(i, {
      erpId: emp?.erpId || null,
      name: emp?.fullName || "",
      email: emp?.email || "",
      role: emp?.employeeId || "",
      ratePerHourUSD: emp?.perHourUSD || 0,
    });
  };
  const addResourceRow = () =>
    onChange({
      ...value,
      assignedResources: [
        ...resources,
        { erpId: null, name: "", email: "", role: "", hours: 0, ratePerHourUSD: 0 },
      ],
    });
  const removeResourceRow = (i: number) =>
    onChange({ ...value, assignedResources: resources.filter((_, idx) => idx !== i) });

  const teamTotal = resources.reduce((s, r) => s + (r.hours || 0) * (r.ratePerHourUSD || 0), 0);
  const isEdit = !!value._id;
  const accountId = useMemo(() => {
    if (!value.upworkAccount) return "";
    if (typeof value.upworkAccount === "object") return value.upworkAccount._id;
    return value.upworkAccount as unknown as string;
  }, [value.upworkAccount]);

  const setField = <K extends keyof CostConfig>(
    key: K,
    val: CostConfig[K] | any
  ) => {
    onChange({ ...value, [key]: val });
  };

  const updateResource = (
    key: "fixedCost" | "graphicDesignerCost" | "coordinatorCost",
    patch: Partial<ResourceValueDoc>
  ) => {
    const current = (value[key] as ResourceValueDoc) || blankResource();
    onChange({ ...value, [key]: { ...current, ...patch } });
  };


  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-sm"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col"
      >

        <div className="p-5 border-b border-slate-200 bg-gradient-to-br from-blue-50/40 to-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={20} className="text-[#0058c3]" />
            <h2 className="text-[18px] font-extrabold text-slate-900">
              {isEdit ? "Edit Cost Config" : "New Cost Config"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <Section title="Identity">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Project Type *">
                <select
                  value={value.projectType || ""}
                  onChange={(e) => setField("projectType", e.target.value)}
                  className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                >
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              {activeSubTypes.length > 0 && (
                <Field label="Sub-Type" hint="Empty = applies to whole type">
                  <select
                    value={value.projectSubType || ""}
                    onChange={(e) => setField("projectSubType", e.target.value)}
                    className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                  >
                    <option value="">All sub-types</option>
                    {activeSubTypes.map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Client Tier *">
                <select
                  value={value.clientTier || "Standard"}
                  onChange={(e) =>
                    setField("clientTier", e.target.value as Tier)
                  }
                  className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Freelance Account"
                hint="Empty = applies to ALL accounts"
              >
                <select
                  value={accountId}
                  onChange={(e) =>
                    setField("upworkAccount", e.target.value || null)
                  }
                  className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                >
                  <option value="">Global (all accounts)</option>
                  {accounts.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>

          <Section title="Pricing">
            <ResourceEditor
              label="Fixed Cost"
              value={value.fixedCost || blankResource()}
              onChange={(p) => updateResource("fixedCost", p)}
            />
          </Section>

          <Section title="Team Resources (ERP rates)">
            <p className="text-[11px] text-slate-500 mb-2">
              Assign who works on this category. Per-hour cost is pulled from ERP payroll
              (net salary ÷ working days ÷ 8h).
            </p>
            {resources.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic mb-2">
                No team members assigned yet.
              </p>
            ) : (
              <div className="space-y-2">
                {resources.map((r, i) => {
                  const lineCost = (r.hours || 0) * (r.ratePerHourUSD || 0);
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 bg-slate-50 rounded-md">
                      <div className="col-span-5">
                        <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Employee</label>
                        <select
                          value={r.email}
                          onChange={(e) => pickEmployee(i, e.target.value)}
                          className="w-full px-2 py-1.5 text-[12px] bg-white border border-slate-200 rounded"
                        >
                          <option value="">Select…</option>
                          {employees.map((e) => (
                            <option key={e.erpId || e.email} value={e.email}>
                              {e.fullName} {e.employeeId ? `(${e.employeeId})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">$/hr</label>
                        <div className="px-2 py-1.5 text-[12px] bg-slate-100 border border-slate-200 rounded text-slate-600">
                          {usd(r.ratePerHourUSD)}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Hours</label>
                        <input
                          type="number"
                          min={0}
                          value={r.hours}
                          onChange={(e) => updateResourceRow(i, { hours: Number(e.target.value) || 0 })}
                          className="w-full px-2 py-1.5 text-[12px] bg-white border border-slate-200 rounded text-right"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Cost</label>
                        <div className="px-2 py-1.5 text-[12px] font-semibold bg-white border border-slate-200 rounded text-right">
                          {usd(lineCost)}
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          onClick={() => removeResourceRow(i)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={addResourceRow}
                className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add team member
              </button>
              <div className="text-[12px] font-bold text-slate-700 inline-flex items-center gap-1.5">
                <Users size={13} className="text-[#0058c3]" /> Team Cost: {usd(teamTotal)}
              </div>
            </div>
          </Section>

          <Section title="Fees & Commission">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Account Holder Commission (%)">
                <input
                  type="number"
                  value={value.commission ?? 0}
                  onChange={(e) =>
                    setField("commission", Number(e.target.value) || 0)
                  }
                  className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                />
              </Field>
              <Field label="Upwork Fee (%)">
                <input
                  type="number"
                  value={value.upworkFee ?? 0}
                  onChange={(e) =>
                    setField("upworkFee", Number(e.target.value) || 0)
                  }
                  className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                />
              </Field>
              <Field label="Target Profit Margin (%)" hint="Projects below this show as 'Below Target'">
                <input
                  type="number"
                  value={value.profitMarginPct ?? 0}
                  onChange={(e) =>
                    setField("profitMarginPct", Number(e.target.value) || 0)
                  }
                  className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                />
              </Field>
            </div>
            <div className="grid grid-cols-12 gap-2 mt-3">
              <div className="col-span-5">
                <Field label="Other Fee — Label">
                  <input
                    placeholder="e.g. Tax, Tech fee"
                    value={value.otherFees?.label || ""}
                    onChange={(e) =>
                      setField("otherFees", {
                        ...(value.otherFees || { amount: 0, type: "fixed" }),
                        label: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                  />
                </Field>
              </div>
              <div className="col-span-4">
                <Field label="Amount">
                  <input
                    type="number"
                    value={value.otherFees?.amount ?? 0}
                    onChange={(e) =>
                      setField("otherFees", {
                        ...(value.otherFees || { label: "", type: "fixed" }),
                        amount: Number(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                  />
                </Field>
              </div>
              <div className="col-span-3">
                <Field label="Type">
                  <select
                    value={value.otherFees?.type || "fixed"}
                    onChange={(e) =>
                      setField("otherFees", {
                        ...(value.otherFees || { label: "", amount: 0 }),
                        type: e.target.value as ResourceValueDoc["type"],
                      })
                    }
                    className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
                  >
                    <option value="fixed">Fixed</option>
                    <option value="percentage">%</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Notes">
            <textarea
              rows={3}
              value={value.notes || ""}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Internal notes for this configuration..."
              className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md resize-none"
            />
          </Section>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50/50 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-[12px] font-semibold border border-slate-200 text-slate-700 rounded-md hover:bg-white"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 text-[12px] font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Save size={14} />
            {saving ? "Saving..." : isEdit ? "Save changes" : "Create config"}
          </button>
        </div>
      </aside>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div>
    <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
      {title}
    </h3>
    <div className="bg-white rounded-lg border border-slate-100 p-3">
      {children}
    </div>
  </div>
);

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div>
    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
      {label}
    </label>
    {children}
    {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const ResourceEditor = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ResourceValueDoc;
  onChange: (patch: Partial<ResourceValueDoc>) => void;
}) => (
  <div className="grid grid-cols-12 gap-2 items-end">
    <div className="col-span-12 -mb-1">
      <label className="block text-[11px] font-semibold text-slate-700">
        {label}
      </label>
    </div>
    <div className="col-span-4">
      <input
        type="number"
        value={value.amount}
        onChange={(e) => onChange({ amount: Number(e.target.value) || 0 })}
        placeholder="Amount"
        className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
      />
    </div>
    <div className="col-span-4">
      <select
        value={value.type}
        onChange={(e) =>
          onChange({ type: e.target.value as ResourceValueDoc["type"] })
        }
        className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md"
      >
        <option value="fixed">Fixed</option>
        <option value="hourly">Hourly</option>
        <option value="percentage">%</option>
      </select>
    </div>
    <div className="col-span-4">
      <input
        type="number"
        value={value.hours}
        onChange={(e) => onChange({ hours: Number(e.target.value) || 0 })}
        placeholder="Hours"
        disabled={value.type !== "hourly"}
        className="w-full px-3 py-2 text-[13px] bg-slate-50 border border-slate-200 rounded-md disabled:opacity-50"
      />
    </div>
  </div>
);

export default CostConfigDrawer;
